import { useEffect, useRef, useState } from 'react';
import type { Job } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { extractErrorCode } from '../lib/dom.js';
import {
  createShareMediaSession,
  destinationKey,
  sendMediaToDestination,
  type ShareMediaSession,
} from '../lib/shareMedia.js';
import { useSession } from '../state/session.js';
import { friendToDestination, groupToDestination, type ShareDestination } from './ShareModal.js';
import { Modal } from './Modal.js';

export interface ShareMediaModalProps {
  open: boolean;
  /** The finished generation the photo/video belongs to. */
  job: Job;
  /** Which of the job's results to send. */
  resultIndex: number;
  onClose: () => void;
}

/**
 * Sends a generated photo or video to a friend or group chat. The file is first
 * stored as a chat attachment, then delivered through the regular message
 * endpoints, so blocks, membership and delivery rules are the usual ones.
 */
export function ShareMediaModal({ open, job, resultIndex, onClose }: ShareMediaModalProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const [destinations, setDestinations] = useState<ShareDestination[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<ShareMediaSession>(createShareMediaSession());
  const busyRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    sessionRef.current = createShareMediaSession();
    setSent(new Set());
    setSendingKey(null);
    setError(null);
    setLoadFailed(false);
    setLoading(true);
    let cancelled = false;
    Promise.all([
      api.listDirectChatFriends().catch(() => null),
      api.listDirectChatGroups().catch(() => null),
    ])
      .then(([friends, groups]) => {
        if (cancelled) return;
        if (!friends && !groups) {
          setLoadFailed(true);
          return;
        }
        setDestinations([
          ...(friends?.friends ?? []).map(friendToDestination),
          ...(groups?.groups ?? []).map(groupToDestination),
        ]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, job.id, resultIndex]);

  async function send(dest: ShareDestination) {
    const key = destinationKey(dest);
    if (busyRef.current || sent.has(key)) return;
    busyRef.current = true;
    setSendingKey(key);
    setError(null);
    try {
      await sendMediaToDestination(api, sessionRef.current, { jobId: job.id, resultIndex }, dest);
      setSent((prev) => new Set(prev).add(key));
    } catch (err: unknown) {
      setError(t(('error.' + extractErrorCode(err)) as Parameters<typeof t>[0]));
    } finally {
      busyRef.current = false;
      setSendingKey(null);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('share.mediaTitle')}
      closeLabel={t('common.close')}
      className="share-modal"
    >
      <div className="share-modal-body">
        <div className="share-destinations-section">
          <h3 className="share-destinations-title">{t('share.sendToChat')}</h3>
          {loading ? (
            <div className="share-destinations-loading">
              <span className="spinner" />
              <span>{t('share.loadingDestinations')}</span>
            </div>
          ) : loadFailed ? (
            <p className="bubble-error">{t('common.error')}</p>
          ) : destinations.length === 0 ? (
            <p className="share-destinations-empty">{t('share.noDestinations')}</p>
          ) : (
            <div className="share-destinations-row" role="region" aria-label={t('share.sendToChat')} tabIndex={0}>
              {destinations.map((dest) => {
                const key = destinationKey(dest);
                const isSending = sendingKey === key;
                const isSent = sent.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className="share-destination-item"
                    disabled={sendingKey !== null || isSent}
                    onClick={() => void send(dest)}
                    aria-label={`${dest.name} - ${t('share.sendToChat')}`}
                  >
                    <div className="share-destination-avatar-wrap">
                      {dest.photoUrl ? (
                        <img className="direct-chat-avatar share-destination-avatar" src={dest.photoUrl} alt="" />
                      ) : (
                        <span className="direct-chat-avatar direct-chat-avatar-fallback share-destination-avatar">
                          {dest.initials}
                        </span>
                      )}
                      {isSending && (
                        <span className="share-destination-badge share-destination-badge--sending">
                          <span className="spinner" />
                        </span>
                      )}
                      {isSent && (
                        <span
                          className="share-destination-badge share-destination-badge--sent"
                          aria-label={t('share.sentSuccess')}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    <span className="share-destination-name" title={dest.name}>
                      {dest.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {sendingKey ? (
            <p className="share-destinations-status share-destinations-status--sending" role="status">
              {t('share.sending')}
            </p>
          ) : sent.size > 0 ? (
            <p className="share-destinations-status share-destinations-status--success" role="status">
              {t('share.sentSuccess')}
            </p>
          ) : null}
          {error ? (
            <p className="bubble-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
